/**
 * Scanning a route that has already been saved.
 *
 * The planner is where a scan normally happens, but an operator who saved a
 * route without running one had no way back to it short of reopening the
 * planner — the dock could show stored findings and could not fetch any. Every
 * scan is a question about a line and nothing else, and the line is on the
 * mission, so there is no reason the planner has to be open to ask it.
 *
 * The three scanners take a `PlannerState`, so a mission is dressed up as one.
 * That is cheaper than teaching each hook a second input shape, and it keeps
 * one code path for what a scan is.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mission, saveMissionRouteIntel } from '../../../services/missionApi';
import { useRouteAssets } from '../useRouteAssets';
import { useRouteEvents } from '../useRouteEvents';
import { useRouteRisk } from '../useRouteRisk';
import { useRouteWeather } from '../useRouteWeather';
import {
  DistanceUnits,
  PlannerState,
  RouteProfile,
  RouteResult,
  RouteRiskSettings,
  Waypoint,
} from '../types';
import {
  RouteIntel,
  ScanHook,
  missionIntel,
  persistablePayload,
  scannedMissionIntel,
} from './intel';

const NO_EXCLUSIONS = { toll: false, motorway: false, ferry: false, unpaved: false };

/**
 * A mission wearing the planner's clothes.
 *
 * Only the parts the scanners read are filled in: the selected route's
 * geometry, the stops, the departure time and the units. Legs are left empty
 * because the turn-by-turn is not re-derived here — the Directions tab reads
 * what the planner stored, and inventing an empty leg list would overwrite it.
 */
export function plannerStateForMission(
  mission: Mission | null,
  units: DistanceUnits,
): PlannerState {
  const geometry = mission?.route_geometry || null;
  const usable = (geometry?.coordinates?.length || 0) >= 2;

  const routes: RouteResult[] = usable && geometry
    ? [{
      id: `mission-${mission?.id ?? 0}`,
      distance: mission?.route_stats?.distance ?? 0,
      duration: mission?.route_stats?.duration ?? 0,
      geometry,
      legs: [],
    }]
    : [];

  const waypoints: Waypoint[] = (mission?.waypoints || []).map((w) => ({
    id: String(w.id),
    longitude: w.longitude,
    latitude: w.latitude,
    label: w.label || undefined,
    labelStatus: 'done',
    source: 'mission',
    notes: w.notes || undefined,
  }));

  return {
    waypoints,
    options: {
      profile: (mission?.route_profile as RouteProfile) || 'driving-traffic',
      exclude: NO_EXCLUSIONS,
      // Saved by the planner alongside the route. Events are the one scan that
      // depends on it: it decides which of them we are actually there for.
      departAt: (mission?.route_options as any)?.departAt ?? null,
      units,
    },
    primaryIndex: 0,
    // The mission's alternate is stored on the mission, not derived from a
    // Mapbox response, so there is nothing here for this index to point at.
    alternateIndex: null,
    primaryName: typeof mission?.route_options?.route_name === 'string'
      ? mission.route_options.route_name
      : '',
    alternateName: mission?.alt_route?.label || '',
    restoreAlternateKey: null,
    phase: routes.length ? 'ready' : 'idle',
    routes,
    hoverIndex: null,
    failedLegs: [],
    error: null,
    past: [],
    future: [],
    savedRouteId: null,
    dirty: false,
    requestSeq: 0,
  };
}

export interface MissionScan {
  intel: RouteIntel;
  hasDepartAt: boolean;
  placesRadiusM: number;
  setPlacesRadiusM: (metres: number) => void;
  eventsCorridorM: number;
  setEventsCorridorM: (metres: number) => void;
  eventsHorizonHours: number;
  setEventsHorizonHours: (hours: number) => void;
  eventsWindowStart: string;
  eventsWindowEnd: string;
  setEventsWindowStart: (iso: string) => void;
  setEventsWindowEnd: (iso: string) => void;
  corridorSettings: RouteRiskSettings;
  setCorridorSettings: (next: Partial<RouteRiskSettings>) => void;
}

export interface UseMissionScanOptions {
  units?: DistanceUnits;
  /**
   * Called after findings have been written to the mission. Hosts that hold a
   * copy of the mission use it to pick up the stored intel; skipping it is
   * safe, since the scan is already on screen either way.
   */
  onPersisted?: (missionId: number) => void;
}

/**
 * Scanners bound to a saved mission, with results kept on it.
 *
 * A scan run here is persisted without being asked to be. In the planner
 * saving is an explicit act with a button behind it, but there is no such
 * moment when reading a mission — an operator who pulls the OSM list, reads
 * it, and clicks away would otherwise have spent a third-party round trip on
 * nothing.
 */
export function useMissionScan(
  mission: Mission | null,
  options: UseMissionScanOptions = {},
): MissionScan {
  const units = options.units || 'mi';
  const missionId = mission?.id ?? null;

  const state = useMemo(
    () => plannerStateForMission(mission, units),
    [mission, units],
  );

  const assets = useRouteAssets(state);
  const events = useRouteEvents(state);
  const weather = useRouteWeather(state);
  const risk = useRouteRisk(state);

  /**
   * Which mission the held reports describe.
   *
   * The scanners outlive the selection, so without this a report pulled for
   * one mission would be shown under the next one clicked. It would be flagged
   * stale — the geometry differs — but "for an earlier version of this route"
   * is the wrong claim entirely when it is a different route.
   */
  const [scannedId, setScannedId] = useState<number | null>(null);
  const owned = scannedId != null && scannedId === missionId;

  // Disarmed on every change of mission. Left armed, selecting a mission would
  // silently spend an Overpass query and a web search on it, which is not
  // something a click on a list should do.
  useEffect(() => {
    assets.setEnabled(false);
    events.setEnabled(false);
    weather.setEnabled(false);
    risk.setEnabled(false);
    setScannedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId]);

  const claim = useCallback(<T,>(hook: ScanHook<T>, mine: boolean): ScanHook<T> => ({
    report: mine ? hook.report : null,
    loading: mine ? hook.loading : false,
    error: mine ? hook.error : null,
    stale: mine ? hook.stale : false,
    capturedAt: mine ? hook.capturedAt : null,
    enabled: mine ? hook.enabled : false,
    setEnabled: (on: boolean) => {
      if (on && missionId != null) setScannedId(missionId);
      hook.setEnabled(on);
    },
    refresh: () => {
      if (missionId != null) setScannedId(missionId);
      hook.refresh();
    },
  }), [missionId]);

  const intel = useMemo(() => scannedMissionIntel(
    missionIntel(mission?.route_intel as any, {
      stale: mission?.route_intel_stale,
      units,
    }),
    {
      assets: claim(assets, owned),
      events: claim(events, owned),
      weather: claim(weather, owned),
      risk: claim(risk, owned),
    },
  ), [mission, units, claim, owned, assets, events, weather, risk]);

  // Keyed on capture times rather than on the reports themselves: a report is
  // a new object on every render, and re-saving an unchanged scan would write
  // to the mission on a loop.
  const captures = `${missionId}|${assets.capturedAt}|${events.capturedAt}|${weather.capturedAt}|${risk.capturedAt}`;
  const written = useRef<string | null>(null);

  useEffect(() => {
    if (missionId == null || !owned) return;
    if (written.current === captures) return;
    const findings = persistablePayload(intel);
    if (Object.keys(findings).length === 0) return;
    written.current = captures;
    saveMissionRouteIntel(missionId, findings as any)
      .then(() => options.onPersisted?.(missionId))
      .catch(() => {
        // The findings are on screen regardless. Failing to keep a copy is not
        // worth an error banner over a scan the operator can simply run again.
        written.current = null;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captures, owned]);

  return {
    intel,
    hasDepartAt: !!state.options.departAt,
    placesRadiusM: assets.radiusM,
    setPlacesRadiusM: assets.setRadiusM,
    eventsCorridorM: events.corridorM,
    setEventsCorridorM: events.setCorridorM,
    eventsHorizonHours: events.horizonHours,
    setEventsHorizonHours: events.setHorizonHours,
    eventsWindowStart: events.windowStart,
    eventsWindowEnd: events.windowEnd,
    setEventsWindowStart: events.setWindowStart,
    setEventsWindowEnd: events.setWindowEnd,
    corridorSettings: risk.settings,
    setCorridorSettings: risk.setSettings,
  };
}

export default useMissionScan;
